import { atom } from "jotai";

/**
 * Suggested reply
 */
interface SuggestedReplyType {
  sendAs: "helena" | "user";
  messageMode: "public" | "private";
  messageTone: "formal" | "informal";
  suggestedMessages: string[];
  selectedMessageIndex: number | null;
  message: string | null;
  mode: "view" | "edit";
  /** ISO timestamp of the currently displayed reply; refreshed on every
   * refine/regenerate/apply so the "Last updated" label stays accurate. */
  updatedAt: string | null;
}

const defaultSuggestedReplyValue: SuggestedReplyType = {
  sendAs: "user",
  messageMode: "public",
  messageTone: "formal",
  suggestedMessages: [],
  selectedMessageIndex: null,
  mode: "view",
  message: null,
  updatedAt: null,
};

const suggestedReplyAtom = atom<SuggestedReplyType>(defaultSuggestedReplyValue);

export const suggestedReply = atom((get) => get(suggestedReplyAtom));
export const setSuggestedReply = atom(
  null,
  (get, set, value: Partial<SuggestedReplyType>) => {
    const currentState = get(suggestedReplyAtom);
    set(suggestedReplyAtom, { ...currentState, ...value });
  },
);

/**
 * Latest unseen suggested reply pushed over MQTT. Held separately from
 * `suggestedReply` so a live update never silently overwrites what the agent
 * is reading/editing — the UI surfaces a refresh affordance instead, and the
 * value is applied only on explicit user action. `null` = nothing pending.
 */
const pendingSuggestedReplyAtom = atom<string | null>(null);

export const pendingSuggestedReply = atom((get) =>
  get(pendingSuggestedReplyAtom),
);
export const setPendingSuggestedReply = atom(
  null,
  (get, set, value: string | null) => {
    set(pendingSuggestedReplyAtom, value);
  },
);

/**
 * `messageId` of the most recent suggested-reply MQTT event that has been
 * processed. Persisted here (not in a component ref) so the dedup survives
 * remounts — switching tabs and returning must not re-surface a reply the
 * user already applied/dismissed. `null` = nothing processed yet.
 */
const lastSuggestedReplyMessageIdAtom = atom<string | null>(null);

export const lastSuggestedReplyMessageId = atom((get) =>
  get(lastSuggestedReplyMessageIdAtom),
);
export const setLastSuggestedReplyMessageId = atom(
  null,
  (get, set, value: string | null) => {
    set(lastSuggestedReplyMessageIdAtom, value);
  },
);

/**
 * common stores
 */
const serviceDetailsAtom = atom<Record<string, any> | null>(null);

export const serviceDetails = atom((get) => get(serviceDetailsAtom));
export const setServiceDetails = atom(
  null,
  (get, set, value: Record<string, any>) => {
    set(serviceDetailsAtom, value);
  },
);

export const resetHelenaStore = atom(null, (get, set) => {
  set(suggestedReplyAtom, defaultSuggestedReplyValue);
  set(pendingSuggestedReplyAtom, null);
  set(lastSuggestedReplyMessageIdAtom, null);
  set(serviceDetailsAtom, null);
});
